import { useLayoutEffect } from 'react';
import { TextInput } from '@components/shared/form/TextInput';
import { useFormContext } from '@context/FormContext';

import CustomSelect from '@components/shared/form/CustomSelectInput';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { countryStates } from '@shared/constants/countryStates';
import { Screens } from '@shared/constants/screens';
import useForm, { validate } from '@shared/hooks/useForm';
import { CDX_ACTIONS, ILicensePlateInfoForm } from 'types/form';
import { getMileages } from '@shared/constants/mileages';
import { postPlateToVIN } from '@services/postPlateToVIN';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { convertArrayToSnakeCase } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';
import { PriceInput } from '@components/shared/form/PriceTextInput';

const EnterLicensePlateForm = () => {
  const { t } = useTexts();
  const { trackClick, trackFormSubmit, trackPage, startInteraction } = useAnalytics(SEGMENT_SCREEN_PARAMS.VEHICLE_SELECTION);
  const milleages = getMileages();
  const { currentStep, licensePlateInfoResponse, contactDetails, updateLicensePlateInfoFormData, handleStep, licensePlateInfoForm, isLoading, submitStart, dispatch, resetForm } =
    useFormContext();
  const { plateNumber, plateState, estimatedMileageRange ,currentMonthlyPayment } = licensePlateInfoForm;
  const { next } = useScreenNavigator();
  const { errors, handleChange, handleSubmit } = useForm<ILicensePlateInfoForm>({
    initialValues: {
      plateNumber,
      plateState,
      estimatedMileageRange,
      currentMonthlyPayment,
    },
    validationSchema: {
      plateNumber: validate.string('licensePlateNumber').required('License Plate Number is required'),
      plateState: validate.string('plateState').required('State is required'),
      estimatedMileageRange: validate.string('estimatedMileage').required('Estimated Mileage is required'),
      currentMonthlyPayment: validate
        .number('currentMonthlyPayment')
        .minNumber(1, t('enterLicensePlate.validation.currentMonthlyPayment.required'))
        .isNumber(t('enterLicensePlate.validation.currentMonthlyPayment.isNumber')),
    },

    onSubmit: async () => {
      if (licensePlateInfoForm.estimatedMileageRange === 'Over 150,000 miles') {
        next(currentStep, 'preSubmitOutcome', {
          estimatedMileageRange: licensePlateInfoForm.estimatedMileageRange,
        }, false);
        trackFormSubmit('Vehicle Information Provided', {
          submission_method: 'auto',
          form_context: 'vehicle_lookup',
          qualified_status: false,
          qualified_fail_reason: 'estimated_mileage',
          ...licensePlateInfoForm,
        });
      } else {
        submitStart();
        const submitResponse = await postPlateToVIN(licensePlateInfoForm);
        if ('error' in submitResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: submitResponse,
          });
          trackFormSubmit('Form Submitted', {
            submission_status: false,
            submission_method: 'auto',
            submission_fail_reason: 'lookup_timeout',
          });
        } else {
          if (
            (!submitResponse?.isSuccess && submitResponse?.message === 'Not Found') ||
            submitResponse?.message === 'No plate match'
          ) {
            dispatch({ type: 'SUBMIT_LICENSE_PLATE_INFO_SUCCESS', payload: null });
            trackFormSubmit('Form Submitted', {
              submission_status: false,
              submission_method: 'auto',
              submission_fail_reason: 'invalid_plate',
            });
          } else {
            dispatch({ type: 'SUBMIT_LICENSE_PLATE_INFO_SUCCESS', payload: submitResponse ?? null });
            const currentYear = new Date().getFullYear();
            if (Number(submitResponse?.year) < currentYear - 10) {
              trackFormSubmit('Vehicle Information Provided', {
                submission_method: 'auto',
                form_context: 'vehicle_lookup',
                qualified_status: false,
                qualified_fail_reason: 'year',
                ...licensePlateInfoForm,
              });
            } else {
              trackFormSubmit('Form Submitted', {
                submission_status: true,
                submission_method: 'auto',
                forward_screen_name: 'prove_identity',
              });

              trackFormSubmit('Vehicle Information Provided', {
                submission_method: 'auto',
                form_context: 'vehicle_lookup',
                qualified_status: true,
                ...licensePlateInfoForm,
              });
            }
          }
        }
        next(currentStep, 'postSubmitOutcome', { apiResponse: submitResponse}, false);
      }
    },
    onValidationError: (errors) => {
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'auto',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  const handleInputChange = <K extends keyof ILicensePlateInfoForm>(field: K, value: ILicensePlateInfoForm[K]) => {
    handleChange(field, value);
    updateLicensePlateInfoFormData({ [field]: value });
  };

  useLayoutEffect(() => {
    if (licensePlateInfoResponse?.vin) {
      next(currentStep, 'onLoad', { vin: licensePlateInfoResponse?.vin }, false);
    } else if (licensePlateInfoForm?.estimatedMileageRange) {
      next(currentStep, 'onLoad', { estimatedMileageRange: licensePlateInfoForm?.estimatedMileageRange }, false);
    } else {
      next(currentStep, 'onLoad', { licensePlateInfoResponse }, false);
    }

    if (!licensePlateInfoResponse && !contactDetails?.contactId && !contactDetails?.leadId) {
      resetForm();
    }
  }, []);


  useEffectOnce(() => trackPage(t('pageTitles.EnterLicensePlate')));

  const handleLicensePlateManually = () => {
    trackClick('Enter vehicle info manually clicked');
    handleStep(Screens.ENTER_LICENSE_PLATE_MANUALLY);
  };

  return (
    <div>
      <form
        className="iLendingCDX-flex-center"
        onSubmit={handleSubmit}
        onFocus={startInteraction}
      >
        <h1 className="iLendingCDX-form-title">{t('enterLicensePlate.title1')}</h1>

        <div>
          <TextInput
            label={t('enterLicensePlate.form.enterLicensePlateNumber')}
            errorMessage={errors.plateNumber}
            onChange={(e) => handleInputChange('plateNumber', e.target.value)}
          />
          <Button
            variant="ghost"
            className="iLendingCDX-mt-2 iLendingCDX-mb-2 ilendingCDX-font-small"
            size="md"
            onClick={handleLicensePlateManually}
          >
            {t('enterLicensePlate.form.enterVehicleInfoManually')}{' '}
            <span style={{ textDecoration: 'underline' }}>{t('enterLicensePlate.form.clickHere')}</span>
          </Button>
          <CustomSelect
            label={t('enterLicensePlate.form.state')}
            className={'iLendingCDX-mb-4'}
            options={countryStates}
            errorMessage={errors.plateState}
            placeholder={t('enterLicensePlate.form.state')}
            value={licensePlateInfoForm.plateState}
            onChange={(e) => handleInputChange('plateState', e.target.value)}
          />
          <CustomSelect
            label={t('enterLicensePlate.form.selectEstimatedMileage')}
            className={'iLendingCDX-mb-6'}
            options={milleages}
            placeholder={t('enterLicensePlate.form.selectEstimatedMileage')}
            value={licensePlateInfoForm.estimatedMileageRange}
            errorMessage={errors.estimatedMileageRange}
            onChange={(e) => handleInputChange('estimatedMileageRange', e.target.value)}
          />
          <PriceInput
            label={t('enterLicensePlate.form.currentMonthlyPayment')}
            className={'iLendingCDX-mb-6'}
            errorMessage={errors.currentMonthlyPayment}
            value={licensePlateInfoForm.currentMonthlyPayment}
            onChange={(value) =>
              handleInputChange('currentMonthlyPayment', Number(value) as ILicensePlateInfoForm['currentMonthlyPayment'])
            }
          />
          <Button
            type="submit"
            className="iLendingCDX-button--rounded iLendingCDX-mb-4"
            size="lg"
            isLoading={isLoading}
          >
            {t('enterLicensePlate.form.continue')}
          </Button>
          <p className="iLendingCDX-para">{t('enterLicensePlate.form.findLicensePlateNumber')}</p>
        </div>
      </form>
    </div>
  );
};
export default EnterLicensePlateForm;
